import { Code, ConnectError, Interceptor } from '@connectrpc/connect';
import { startRpcSpan } from './sentry-lazy';
import { getResourceTimings } from './resource-timing';

/**
 * Sentry tracing interceptor.
 *
 * Every RPC is traced as a child span of an explicitly chosen parent —
 * the live pageload/navigation root, or a shared route-visit container
 * transaction (see route-container.ts) — via startRpcSpan. Explicit
 * parenting sidesteps the browser SDK's leaked "active span" pointer
 * (no async-context tracking), and nesting under a shared container
 * keeps every RPC recorded without billing a Sentry transaction per RPC.
 */
export const connectTracingInterceptorFactory =
  (env: { apiUrl: string }): Interceptor =>
  (next) =>
  async (req) => {
    const fullUrl = `${env.apiUrl}/${req.service.typeName}/${req.method.name}`;

    return await startRpcSpan(
      {
        op: 'http.client',
        name: `POST ${fullUrl}`,
      },
      async (span) => {
        // Use standard HTTP attributes to fit in with Sentry
        const { hostname, port } = new URL(env.apiUrl);
        span.setAttribute('http.request.method', 'POST');
        span.setAttribute('server.address', hostname);
        span.setAttribute('server.port', port || undefined);

        // Wrap `await next(req)` in performance.now() bookmarks so we can
        // attach a per-phase breakdown derived from PerformanceResourceTiming.
        // Investigating browser-side gap between LB latency (≤0.75s in prod)
        // and Sentry span p99 (multi-second). perf.post_fetch_ms surfaces
        // main-thread / microtask delay directly.
        const wallStart = performance.now();
        try {
          const res = await next(req);
          span.setAttribute('http.response.status_code', 200);
          return res;
        } catch (err) {
          const status =
            err instanceof ConnectError
              ? (CONNECT_CODE_TO_HTTP_STATUS[err.code] ?? 500)
              : 500;
          span.setAttribute('http.response.status_code', status);
          throw err;
        } finally {
          const spanWallMs = performance.now() - wallStart;
          for (const [key, value] of Object.entries(
            getResourceTimings(fullUrl, spanWallMs)
          )) {
            span.setAttribute(key, value);
          }
        }
      }
    );
  };

const CONNECT_CODE_TO_HTTP_STATUS: Partial<Record<Code, number>> = {
  [Code.Canceled]: 499,
  [Code.InvalidArgument]: 400,
  [Code.DeadlineExceeded]: 504,
  [Code.NotFound]: 404,
  [Code.AlreadyExists]: 409,
  [Code.PermissionDenied]: 403,
  [Code.ResourceExhausted]: 429,
  [Code.FailedPrecondition]: 400,
  [Code.Aborted]: 409,
  [Code.OutOfRange]: 400,
  [Code.Unimplemented]: 501,
  [Code.Unavailable]: 503,
  [Code.Unauthenticated]: 401,
  // Unknown, Internal, and DataLoss fall through to the 500 default.
};
